
import { Util } from '@phdsoft/common';
import * as THREE from '@phdsoft/three';
import { Triangle } from '@phdsoft/three';
import { PlaneUtil } from './digital-twin/plane';

export class GeometryUtil {

    public static CreateBoundaries(meshGeometry: THREE.BufferGeometry): any {
        const trianglesIndex = meshGeometry.index;
        const paritySegments = {};
        for (let i = 0; i < trianglesIndex.count; i += 3) {
            //#region First Edge

            let index1 = trianglesIndex.array[i];
            let index2 = trianglesIndex.array[i + 1];

            let key = Math.min(index1, index2) + "," + Math.max(index1, index2);

            if (key in paritySegments) {
                const parity = paritySegments[key];
                paritySegments[key] = parity + 1;
            }
            else {
                paritySegments[key] = 1;
            }

            //#endregion

            //#region Second Edge

            index1 = trianglesIndex.array[i];
            index2 = trianglesIndex.array[i + 2];

            key = Math.min(index1, index2) + "," + Math.max(index1, index2);

            if (key in paritySegments) {
                const parity = paritySegments[key];
                paritySegments[key] = parity + 1;
            }
            else {
                paritySegments[key] = 1;
            }

            //#endregion

            //#region Third Edge

            index1 = trianglesIndex.array[i + 1];
            index2 = trianglesIndex.array[i + 2];

            key = Math.min(index1, index2) + "," + Math.max(index1, index2);

            if (key in paritySegments) {
                const parity = paritySegments[key];
                paritySegments[key] = parity + 1;
            }
            else {
                paritySegments[key] = 1;
            }

            //#endregion
        }

        const sequencyList = [];

        for (const pairKey in paritySegments) {
            if (paritySegments[pairKey] === 1) {
                const keys = pairKey.split(',');
                if (keys.length === 2) {
                    sequencyList.push(Number(keys[0]));
                    sequencyList.push(Number(keys[1]));
                }
            }
        }

        //#region  Sort the sequency to all edges stay with same orientation

        const sortedSequencyList = [];
        if (sequencyList.length >= 2) {
            sortedSequencyList.push(sequencyList[0]);
            sortedSequencyList.push(sequencyList[1]);

            sequencyList.splice(0, 2);

            while (sequencyList.length != 0) {
                const pos = sequencyList.indexOf(sortedSequencyList[sortedSequencyList.length - 1]);
                if (pos === -1) {
                    sortedSequencyList.push(sequencyList[0]);
                    sortedSequencyList.push(sequencyList[1]);
                    sequencyList.splice(0, 2);
                }
                else if (pos % 2 === 0) {
                    sortedSequencyList.push(sequencyList[pos]);
                    sortedSequencyList.push(sequencyList[pos + 1]);
                    sequencyList.splice(pos, 2);
                }
                else {
                    sortedSequencyList.push(sequencyList[pos]);
                    sortedSequencyList.push(sequencyList[pos - 1]);
                    sequencyList.splice(pos - 1, 2);
                }
            }
        }

        //#endregion

        const boundariesIndex = [];
        while (sortedSequencyList.length > 0) {
            const initIndex = sortedSequencyList[0];
            const boundaryList = [];
            boundaryList.push(initIndex);
            let i;
            for (i = 1; i < sortedSequencyList.length; i++) {
                const index = sortedSequencyList[i];
                boundaryList.push(index);
                if (index === initIndex) {
                    break;
                }
            }

            let boundary = [];
            boundary = boundaryList.slice();
            boundariesIndex.push(boundary);

            i = Math.min(i, sortedSequencyList.length - 1);
            for (let j = 0; j <= i; j++) {
                sortedSequencyList.splice(0, 1);
            }
        }

        const boundaries = [];

        for (const bound of boundariesIndex) {
            const b = [];
            for (const i of bound) {
                b.push(meshGeometry.attributes.position.getX(i));
                b.push(meshGeometry.attributes.position.getY(i));
                b.push(meshGeometry.attributes.position.getZ(i));
            }
            boundaries.push(b);
        }

        return boundaries;
    }

    private static tripleProduct(a, b, c) {
        return a.clone().dot(
            (new THREE.Vector3()).crossVectors(b, c)
        );
    }

    public static IsCoPlanar(a, b, c, d): boolean {
        let ab = b.clone().sub(a);
        let ac = c.clone().sub(a);
        let ad = d.clone().sub(a);
        return this.tripleProduct(ab, ac, ad) === 0;
    }

    public static IsGeometryCoPlanar(geometry: THREE.BufferGeometry): boolean {
        const vectors = [];
        for (let i = 0; i < geometry.attributes.position.count; i++) {
            vectors.push(new THREE.Vector3(
                geometry.attributes.position.getX(i),
                geometry.attributes.position.getY(i),
                geometry.attributes.position.getZ(i)));
        }

        let isCoPlanar = true;
        for (let i = 3; i < vectors.length; i++) {
            isCoPlanar = isCoPlanar && this.IsCoPlanar(vectors[0], vectors[1], vectors[2], vectors[i]);
        }

        return isCoPlanar;
    }

    public static IsGeometryBoxCoPlanar(geometry: THREE.BufferGeometry): boolean {
        geometry.computeBoundingBox();
        let size = new THREE.Vector3();
        geometry.boundingBox.getSize(size);
        const tol = 0.01;
        if (size.x <= tol || size.y <= tol || size.z <= tol) {
            return true;
        }
        return false;
    }

    public static IsGeometryCoPlanarPlane(geometry: THREE.BufferGeometry): boolean {
        const vectors = [];
        for (let i = 0; i < geometry.attributes.position.count; i++) {
            vectors.push(new THREE.Vector3(
                geometry.attributes.position.getX(i),
                geometry.attributes.position.getY(i),
                geometry.attributes.position.getZ(i)));
        }
        if (Util.isNullOrUndefined(vectors[2]))
            return false;
        const planeUtil = new PlaneUtil();
        // const plane = new Plane();
        planeUtil.setFromCoplanarPoints(vectors[0], vectors[1], vectors[2]);
        // if (plane.constant === 0)
        //     return false;
        for (let i = 3; i < vectors.length; i++) {
            const distance = planeUtil.PointDistance(vectors[i]);
            if (distance >= 0.0001) {
                return false;
            }
        }
        return true;
    }

    public static isPointInside(bb: THREE.Box3, point: THREE.Vector3) {
      if (bb.max.x >= point.x && bb.max.y >= point.y && bb.max.z >= point.z &&
        bb.min.x <= point.x && bb.min.y <= point.y && bb.min.z <= point.z) {
        // Point is in bounding box
        return true;
      }
      return false;
    }

    public static computeBoundingBox(group: THREE.Group): THREE.Box3 {
      const bbox = new THREE.Box3();
      for (const _child of group.children) {
        bbox.expandByObject(_child);
      }
      return bbox;
    }

    public static checkifMeshIsInsideBox(mesh: THREE.Mesh, box: THREE.Box3) {
      const geometry: THREE.BufferGeometry = mesh.geometry as THREE.BufferGeometry;
      const _vectors = [];
      const _positionArray = geometry.attributes.position.array;
      for (let i = 0; i < _positionArray.length; i += 3) {
        const x = _positionArray[i],
          y = _positionArray[i + 1],
          z = _positionArray[i + 2];
        _vectors.push(new THREE.Vector3(x, y, z));
      }

      for (const _v of _vectors) {
        if (!box.containsPoint(_v)) {
          return false;
        }
      }
      return true;
    }

    public static computeobjectNormal(object: THREE.Mesh, value: number) {
      const normalVector = new THREE.Vector3();
      const bufferGeometry = object.geometry as THREE.BufferGeometry;
      const _normalArray = bufferGeometry.attributes.normal.array;
      for (let i = 0; i < _normalArray.length; i += 3) {
        const x = _normalArray[i],
          y = _normalArray[i + 1],
          z = _normalArray[i + 2];
        normalVector.add(new THREE.Vector3(x, y, z));
      }
      normalVector.normalize();
      object.userData.ElementNormal = normalVector.clone();
      const box = object.geometry.boundingBox;

      let _n = Util.LargestCanonicalAxisValue(normalVector);
      const normal = new THREE.Vector3(_n[0], _n[1], _n[2]);
      box.max.add(normal.clone().multiplyScalar(value));
      box.min.sub(normal.clone().multiplyScalar(value));
      // box.expandByPoint()
      object.userData.ExpandedBox3 = box;
    }

    public static computeBox(elements) {
      const bbox: THREE.Box3 = new THREE.Box3();
      for (const iterator of elements) {
        bbox.expandByObject(iterator);
      }
      return bbox;
    }

    public static computeGroupBBox(group: THREE.Group) {
      const bbox: THREE.Box3 = new THREE.Box3();
      for (const child of group.children) {
        bbox.expandByObject(child);
      }
      const centerBox = new THREE.Vector3();
      bbox.getCenter(centerBox);

      group.userData.Bbox = bbox;
      group.userData.CenterBox = centerBox;
    }

    public static IsSectionSimilar(A, B): boolean {
      const nA = Util.LargestCanonicalAxisDirection(A.normal);
      const nB = Util.LargestCanonicalAxisDirection(B.normal);
      if (nA === nB) {
        if (nA === 'x') {
          if (Math.abs(A.center.x - B.center.x) <= 3) {
            return true;
          }
        }
        else if (nA === 'y') {
          if (Math.abs(A.center.y - B.center.y) <= 3) {
            return true;
          }
        }
        else if (nA === 'z') {
          if (Math.abs(A.center.z - B.center.z) <= 3) {
            return true;
          }
        }
      }
      return false
    }

    public static clearGroup(group: THREE.Group) {
      const objs = []
      if (group) {
        for (const i in group.children) {
          group.children[i].visible = false;
          objs.push(group.children[i])
        }

        for (const i in objs) {
          objs[i].geometry.dispose()
          objs[i].material.dispose()
          group.remove(objs[i])
          // objs[i].dispose();
        }
      }
    }

    public static getVolume(geometry, center?: THREE.Vector3) {
      if (!geometry.isBufferGeometry) {
        console.log("'geometry' must be an indexed or non-indexed buffer geometry");
        return 0;
      }
      var isIndexed = geometry.index !== null;
      let position = geometry.attributes.position;
      let sum = 0;
      let p1 = new THREE.Vector3(),
        p2 = new THREE.Vector3(),
        p3 = new THREE.Vector3();
      if (!isIndexed) {
        let faces = position.count / 3;
        for (let i = 0; i < faces; i++) {
          p1.fromBufferAttribute(position, i * 3 + 0);
          p2.fromBufferAttribute(position, i * 3 + 1);
          p3.fromBufferAttribute(position, i * 3 + 2);

          const triangle = new Triangle(p1, p2, p3);
          const triangleNormal = new THREE.Vector3();
          const triangleCenter = new THREE.Vector3();

          triangle.getNormal(triangleNormal);
          triangle.getMidpoint(triangleCenter);

          const d1 = triangleCenter.distanceTo(center);
          const d2 = triangleCenter.add(triangleNormal.multiplyScalar(2)).distanceTo(center);

          if (d1 < d2) {
            sum += this.signedVolumeOfTriangle(p1, p2, p3);
          }
          else {
            sum += this.signedVolumeOfTriangle(p1, p3, p2);
          }
        }
      }
      else {
        let index = geometry.index;
        let faces = index.count / 3;
        for (let i = 0; i < faces; i++) {
          p1.fromBufferAttribute(position, index.array[i * 3 + 0]);
          p2.fromBufferAttribute(position, index.array[i * 3 + 1]);
          p3.fromBufferAttribute(position, index.array[i * 3 + 2]);

          const triangle = new Triangle(p1, p2, p3);
          const triangleNormal = new THREE.Vector3();
          const triangleCenter = new THREE.Vector3();

          triangle.getNormal(triangleNormal);
          triangle.getMidpoint(triangleCenter);

          const d1 = triangleCenter.distanceTo(center);
          const d2 = triangleCenter.add(triangleNormal.multiplyScalar(2)).distanceTo(center);

          if (d1 < d2) {
            sum += this.signedVolumeOfTriangle(p1, p2, p3);
          }
          else {
            sum += this.signedVolumeOfTriangle(p1, p3, p2);
          }
        }
      }
      return sum;
    }

    public static signedVolumeOfTriangle(p1, p2, p3) {
      return p1.dot(p2.cross(p3)) / 6.0;
    }

    public static MakeSpiralPoints(structure: any, center: THREE.Vector3, normal: string): Array<{ point, normal }> {
      let size = new THREE.Vector3();
      // structure.geometry.boundingBox.getCenter(center);
      structure.geometry.boundingBox.getSize(size);

      let maxSize = Math.max(...[size.x, size.y, size.z]);
      const numberOfSamplingPoints = 500;
      const maximumRadius = maxSize * 2; // in pixels
      const numberOfRotations = 15;
      const spiralOrientation = 1;
      const spiralPoints = []

      switch (normal) {
          case 'x':
              for (let i = 1; i <= numberOfSamplingPoints; i++) {
                  const angle = 2 * Math.PI * Math.sqrt(1.0 * i / numberOfSamplingPoints) * numberOfRotations;
                  const x = (maximumRadius * i / numberOfSamplingPoints * Math.cos(angle) * spiralOrientation);
                  const y = (maximumRadius * i / numberOfSamplingPoints * Math.sin(angle));

                   const newPoint = center.clone().add(new THREE.Vector3(0, x, y));

                   let raycaster = new THREE.Raycaster(newPoint.clone(), structure.userData.ElementNormal.clone());
                  let invertedRaycaster = new THREE.Raycaster(newPoint.clone(), structure.userData.ElementNormal.clone().multiplyScalar(-1));

                  let intersects = raycaster.intersectObjects([structure]);
                  let intersects2 = invertedRaycaster.intersectObjects([structure]);

                  let resultObject = intersects.length > 0 ? intersects[0] : intersects2.length > 0 ? intersects2[0] : null;
                  if (resultObject) {
                      spiralPoints.push({ point: resultObject.point, normal: resultObject.face.normal });
                  }
              }
              break;
          case 'y':
              for (let i = 1; i <= numberOfSamplingPoints; i++) {
                  const angle = 2 * Math.PI * Math.sqrt(1.0 * i / numberOfSamplingPoints) * numberOfRotations;
                  const x = (maximumRadius * i / numberOfSamplingPoints * Math.cos(angle) * spiralOrientation);
                  const y = (maximumRadius * i / numberOfSamplingPoints * Math.sin(angle));

                   const newPoint = center.clone().add(new THREE.Vector3(x, 0, y));

                   let raycaster = new THREE.Raycaster(newPoint.clone(), structure.userData.ElementNormal.clone());
                  let invertedRaycaster = new THREE.Raycaster(newPoint.clone(), structure.userData.ElementNormal.clone().multiplyScalar(-1));

                  let intersects = raycaster.intersectObjects([structure]);
                  let intersects2 = invertedRaycaster.intersectObjects([structure]);

                  let resultObject = intersects.length > 0 ? intersects[0] : intersects2.length > 0 ? intersects2[0] : null;
                  if (resultObject) {
                      spiralPoints.push({ point: resultObject.point, normal: resultObject.face.normal });
                  }
              }
              break;
          case 'z':
              for (let i = 1; i <= numberOfSamplingPoints; i++) {
                  const angle = 2 * Math.PI * Math.sqrt(1.0 * i / numberOfSamplingPoints) * numberOfRotations;
                  const x = (maximumRadius * i / numberOfSamplingPoints * Math.cos(angle) * spiralOrientation);
                  const y = (maximumRadius * i / numberOfSamplingPoints * Math.sin(angle));

                   const newPoint = center.clone().add(new THREE.Vector3(x, y, 0));

                   let raycaster = new THREE.Raycaster(newPoint.clone(), structure.userData.ElementNormal.clone());
                  let invertedRaycaster = new THREE.Raycaster(newPoint.clone(), structure.userData.ElementNormal.clone().multiplyScalar(-1));

                  let intersects = raycaster.intersectObjects([structure]);
                  let intersects2 = invertedRaycaster.intersectObjects([structure]);

                  let resultObject = intersects.length > 0 ? intersects[0] : intersects2.length > 0 ? intersects2[0] : null;
                  if (resultObject) {
                      spiralPoints.push({ point: resultObject.point, normal: resultObject.face.normal });
                  }
              }
              break;

          default:
              break;
      }
      return spiralPoints;
  }

  public static disposeMeshElement(_mesh: THREE.Mesh): void {
    _mesh.geometry.dispose();
  }

  public static getRingPrincipalAxis(line3: THREE.Line3) : THREE.Vector3[]
  {
    let axisX = new THREE.Vector3(1,0,0);
    let axisY = new THREE.Vector3(0,1,0);
    const axisZ = new THREE.Vector3(0,0,1);
    const epsilon = 0.01;

    const flowVector: THREE.Vector3 = new THREE.Vector3().subVectors(line3.end, line3.start).normalize();
    const inverseFlowVector = flowVector.clone().multiplyScalar(-1)

    let axisYProduct = new THREE.Vector3().crossVectors(flowVector, axisY)
    let axisZProduct = new THREE.Vector3().crossVectors(inverseFlowVector, axisZ)

    if (axisYProduct.length() <= epsilon || axisZProduct.length() <= epsilon)
    {
      axisX = axisX;
      axisY = axisY.crossVectors(axisX, flowVector).normalize()
    }
    else
    {
      axisX = axisX.crossVectors(flowVector, axisZ).normalize();
      axisY = axisY.crossVectors(axisX, flowVector).normalize();
    }

    return [axisX, axisY]
  }

  public static getRingPoint(angle: number, ringCenter: THREE.Vector3, axisX: THREE.Vector3, axisY: THREE.Vector3, radious: number): THREE.Vector3
  {
    let ringVector:THREE.Vector3 = this.getRingVector(angle, axisX, axisY).clone();
    ringVector.normalize();

    let ringPoint: THREE.Vector3 = ringVector.multiplyScalar(radious).clone();
    ringPoint.add(ringCenter);

    return ringPoint;
  }

  private static getRingVector(angle: number, axisX: THREE.Vector3, axisY: THREE.Vector3): THREE.Vector3
  {
    let _axisX = axisX.clone().normalize();
    let _axisY = axisY.clone().normalize();
    let v: THREE.Vector3 = new THREE.Vector3(1,0,0);

    switch (angle)
    {
      case 0: v = _axisX; break;
      case 90: v = _axisY; break;
      case 180: v = _axisX.multiplyScalar(-1); break;
      case 270: v = _axisY.multiplyScalar(-1); break;
      case 45: v = _axisX.add(_axisY); break;
      case 135: v = _axisY.sub(_axisX); break;
      case 225: v = _axisX.add(_axisY).multiplyScalar(-1); break;
      case 315: v = _axisX.sub(_axisY); break;
    }

    v.normalize();

    return v;
  }
}
